import type {
  ChatResponseBodyDto,
  ControllerParamsDto,
  DateRangeDto,
  RawMessageDto,
  RecommendationDto,
  ReportPayloadDto,
  ResponseBlockDto,
  SuggestedPromptDto,
  ThreadHistoryDto,
} from '../models/orpheus.dto'
import type {
  ChatMessage,
  MessageAnswer,
  MessageCredits,
  MessageKind,
  MessageRole,
  Recommendation,
} from '../models/orpheus.types'
import type { Clarification } from '../models/orpheusClarification.types'
import type { SetupParts } from '../models/orpheusSetup.types'

import { normalizeClarification } from './orpheusClarification'
import { EMPTY_SETUP_PARTS, extractSetupParts, hasSetupParts } from './orpheusSetupParts'
import { renderableResponseBlocks } from './responseBlocks'
import {
  arrayOrNull,
  firstFiniteNumber,
  firstRecord,
  firstString,
  firstStringList,
  isRecord,
  recordOrNull,
  stringList,
  stringOrNull,
} from './wireReaders'

export interface MessageIdentity {
  localId: string
  turnId: string
  index: number
}

interface MessageSource {
  role: MessageRole
  content: string
  response: ChatResponseBodyDto | null
  responseType: string | null
  messageId: string | null
  createdAt: string | null
  updatedAt: string | null
  controllerParams: ControllerParamsDto | null
  payload: ReportPayloadDto | null
  recommendations: RecommendationDto[]
  recommendationData: Record<string, unknown> | null
  responseBlocks: ResponseBlockDto[]
  followups: string[]
  suggestedPrompts: SuggestedPromptDto[]
  channels: string[]
  extractedParameters: unknown
  dateRangeCandidate: unknown
  setupParts?: SetupParts
  clarification?: Clarification | null
  credits?: MessageCredits | null
}

export function mapThreadMessage(
  message: RawMessageDto,
  index: number,
  threadLocalId: string,
): ChatMessage {
  const messageId = stringOrNull(message.msg_id) ?? stringOrNull(message.message_id)
  const stableId = messageId ?? `${threadLocalId}:history:${index}`
  const response = recordOrNull(message.response) as ChatResponseBodyDto | null
  const metadata = recordOrNull(message.metadata)
  const responseType = stringOrNull(response?.type) ?? stringOrNull(metadata?.response_type) ?? null
  const content =
    stringOrNull(message.content) ??
    stringOrNull(response?.content) ??
    stringOrNull(recordOrNull(metadata?.response)?.content) ??
    ''
  const controllerParams = asControllerParams(
    message.controller_params ?? metadata?.controller_params,
  )
  const payload = firstReportPayload(
    response?.raw_data,
    message.controller_response,
    metadata?.controller_response,
    recordOrNull(metadata?.response)?.raw_data,
    message.table_data,
  )

  return mapMessageSource(
    {
      role: normalizeRole(message.message_type ?? message.role),
      content,
      response,
      responseType,
      messageId,
      createdAt: stringOrNull(message.created_at) ?? stringOrNull(metadata?.created_at) ?? null,
      updatedAt: stringOrNull(message.updated_at) ?? stringOrNull(metadata?.updated_at) ?? null,
      controllerParams,
      payload,
      recommendations: firstRecommendationList(response?.recommendations, message.recommendations),
      recommendationData: firstRecord(response?.recommendation_data, message.recommendation_data),
      responseBlocks: firstResponseBlocks(
        response?.response_blocks,
        response?.responseBlocks,
        message.response_blocks,
        message.responseBlocks,
        recordOrNull(metadata?.response)?.response_blocks,
        recordOrNull(metadata?.response)?.responseBlocks,
      ),
      followups: firstStringList(response?.followups, message.followups),
      suggestedPrompts: firstSuggestedPrompts(
        response?.suggested_prompts,
        message.suggested_prompts,
        metadata?.suggested_prompts,
      ),
      channels: firstStringList(message.channels, extractChannels(controllerParams)),
      extractedParameters: metadata?.extracted_parameters,
      dateRangeCandidate: metadata?.resolved_date_range,
      setupParts: extractSetupParts(message),
      clarification:
        normalizeClarification(response, metadata) ?? normalizeClarification(message, metadata),
      // What the turn charged, stored with it so a reloaded thread keeps its credits line.
      credits: toMessageCredits(metadata?.credits),
    },
    { localId: stableId, turnId: stableId, index },
  )
}

/** `{ charged, remaining }` from a §1.4 credits block; null when absent (sysadmin/exempt). */
export function toMessageCredits(value: unknown): MessageCredits | null {
  const credits = recordOrNull(value)
  const charged = credits?.charged
  if (typeof charged !== 'number' || !Number.isFinite(charged)) return null
  const remaining = credits?.remaining
  return {
    charged,
    remaining: typeof remaining === 'number' && Number.isFinite(remaining) ? remaining : null,
  }
}

export function mapThreadHistory(history: ThreadHistoryDto, threadLocalId: string): ChatMessage[] {
  return historyMessages(history).map((message, index) =>
    mapThreadMessage(message, index, threadLocalId),
  )
}

export function mapMessageSource(source: MessageSource, identity: MessageIdentity): ChatMessage {
  const answer = source.payload
    ? buildAnswer(
        source.payload,
        source.response,
        source.controllerParams,
        source.dateRangeCandidate,
      )
    : null
  const setupParts = source.setupParts ?? EMPTY_SETUP_PARTS
  const clarification = source.clarification ?? null
  // A setup question or a clarification can arrive with no text of its own: the card or the chips
  // are the answer then, not a missing one.
  const missingAssistantContent =
    source.role === 'assistant' &&
    source.content.trim().length === 0 &&
    !hasSetupParts(setupParts) &&
    !clarification
  const kind = missingAssistantContent
    ? 'error'
    : messageKind(source.responseType, answer, source.recommendations)

  return {
    localId: identity.localId,
    turnId: identity.turnId,
    messageId: source.messageId,
    role: source.role,
    kind,
    content: source.content,
    index: identity.index,
    createdAt: source.createdAt,
    updatedAt: source.updatedAt,
    streamState: 'completed',
    answer,
    recommendations: source.recommendations.map(mapRecommendation),
    recommendationData: source.recommendationData,
    responseBlocks: renderableResponseBlocks(source.responseBlocks),
    followups: source.followups,
    suggestedPrompts: source.suggestedPrompts,
    channels:
      source.channels.length > 0 ? source.channels : extractChannels(source.controllerParams),
    controllerParams: source.controllerParams,
    ...(source.extractedParameters === undefined
      ? {}
      : { extractedParameters: source.extractedParameters }),
    ...(missingAssistantContent ? { errorKey: 'orpheus.error.missingResponse' } : {}),
    setupStep: setupParts.setup,
    setupCard: setupParts.card,
    filePreview: setupParts.filePreview,
    clarification,
    credits: source.credits ?? null,
  }
}

export function firstReportPayload(...candidates: unknown[]): ReportPayloadDto | null {
  for (const candidate of candidates) {
    const direct = asReportPayload(candidate)
    if (direct) return direct

    const nested = asReportPayload(recordOrNull(candidate)?.data)
    if (nested) return nested
  }
  return null
}

function buildAnswer(
  payload: ReportPayloadDto,
  response: ChatResponseBodyDto | null,
  controllerParams: ControllerParamsDto | null,
  dateRangeCandidate: unknown,
): MessageAnswer {
  const responseRecord = recordOrNull(response)
  const visibleTypeHint = response?.visible_type ?? null
  return {
    payload,
    visibleType: visibleTypeHint && visibleTypeHint !== 'table' ? 'chart' : 'table',
    chartTypeHint:
      visibleTypeHint && visibleTypeHint !== 'table' && visibleTypeHint !== 'chart'
        ? visibleTypeHint
        : null,
    selectedMetrics:
      stringList(responseRecord?.preferred_metrics) ?? controllerParams?.metrics ?? [],
    selectedDimensions: controllerParams?.dimensions ?? [],
    dateRange: extractDateRange(payload, controllerParams, dateRangeCandidate),
    resultCount: extractResultCount(payload),
  }
}

function extractDateRange(
  payload: ReportPayloadDto,
  controllerParams: ControllerParamsDto | null,
  dateRangeCandidate: unknown,
): DateRangeDto | null {
  const candidate =
    recordOrNull(payload.date_range) ??
    recordOrNull(controllerParams?.date_range) ??
    recordOrNull(dateRangeCandidate) ??
    null
  if (!candidate) return null

  const start = firstString(
    candidate.start,
    candidate.start_date,
    candidate.startDate,
    candidate.date_start,
    candidate.from,
    candidate.range_start,
  )
  const end = firstString(
    candidate.end,
    candidate.end_date,
    candidate.endDate,
    candidate.date_end,
    candidate.to,
    candidate.range_end,
  )
  const days = firstFiniteNumber(
    candidate.lookback_days,
    candidate.lookbackDays,
    candidate.days,
    candidate.interval_days,
  )
  return start || end || days !== null ? { start, end, days } : null
}

function extractResultCount(payload: ReportPayloadDto): number {
  const candidates = [
    payload.result_count,
    payload.results_count,
    payload.row_count,
    payload.total_count,
    payload.total_rows,
    typeof payload.total === 'number' ? payload.total : null,
    recordOrNull(payload.pagination)?.total,
  ]
  return firstFiniteNumber(...candidates) ?? payload.data.length
}

function extractChannels(controllerParams: ControllerParamsDto | null): string[] {
  const values = controllerParams?.filters
    ?.filter((filter) => filter.field === 'marketing_channel')
    .flatMap((filter) => (Array.isArray(filter.value) ? filter.value : [filter.value]))
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
  return [...new Set(values ?? [])]
}

function mapRecommendation(wire: RecommendationDto): Recommendation {
  return {
    id: wire.recommendation_id ?? wire.id ?? null,
    entity: wire.entity,
    category: wire.category,
    currentMetrics: wire.current_metrics ?? {},
    reasoning: wire.reasoning ?? null,
    suggestedAction: wire.suggested_action ?? null,
    requiresConfirmation: wire.requires_confirmation ?? false,
    confidence: wire.confidence ?? null,
    wire,
  }
}

function messageKind(
  responseType: string | null,
  answer: MessageAnswer | null,
  recommendations: RecommendationDto[],
): MessageKind {
  if (
    responseType === 'error' ||
    responseType === 'timeout' ||
    responseType === 'response_generation_error'
  ) {
    return 'error'
  }
  if (responseType === 'recommendation' || recommendations.length > 0) return 'recommendation'
  if (answer) return 'data'
  return 'text'
}

function historyMessages(history: ThreadHistoryDto): RawMessageDto[] {
  return (
    arrayOrNull(history.messages) ??
    arrayOrNull(history.thread?.messages) ??
    arrayOrNull(history.history) ??
    arrayOrNull(history.data?.messages) ??
    []
  )
}

function asReportPayload(value: unknown): ReportPayloadDto | null {
  const record = recordOrNull(value)
  return record && Array.isArray(record.data) ? (record as ReportPayloadDto) : null
}

function asControllerParams(value: unknown): ControllerParamsDto | null {
  return recordOrNull(value) as ControllerParamsDto | null
}

function normalizeRole(value: unknown): MessageRole {
  return value === 'user' ? 'user' : 'assistant'
}

function firstRecommendationList(...values: unknown[]): RecommendationDto[] {
  for (const value of values) {
    if (Array.isArray(value)) return value.filter(isRecommendation)
  }
  return []
}

function isRecommendation(value: unknown): value is RecommendationDto {
  const record = recordOrNull(value)
  return Boolean(record && typeof record.entity === 'string' && typeof record.category === 'string')
}

function firstResponseBlocks(...values: unknown[]): ResponseBlockDto[] {
  for (const value of values) {
    if (Array.isArray(value)) return value.filter(isRecord) as ResponseBlockDto[]
  }
  return []
}

function firstSuggestedPrompts(...values: unknown[]): SuggestedPromptDto[] {
  for (const value of values) {
    if (Array.isArray(value)) {
      return value.filter(
        (item): item is SuggestedPromptDto => typeof item === 'string' || isRecord(item),
      )
    }
  }
  return []
}
